import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { Row, Col, Card, CardBody } from 'reactstrap';
import axios from 'axios';

// Components
import HelpButton from '../layouts/mainLayout/components/HelpButton';
import ChecksFilterManager from '../tools/components/ChecksFilterManager';
import AlertsResumeTable from '../components/alertsRegistry/AlertsResumeTable';
import DateFilterManager from '../components/alertsRegistry/DateFilterManager';

// Scripts
import { getFilteredArray } from '../tools/scripts/arrayManagement';

class AlertsRegistry extends React.Component {
    constructor() {
        super();

        // Get default date range (1 month ago to today)
        const today = new Date();
        const oneMonthAgo = new Date();
        oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
        
        this.state = {
            activeQuantity: 5,
            fromDate: oneMonthAgo,
            toDate: today,
            filteredAlerts: [],
            originalAlerts: [],
            alertTypeFilters: {},
            alertTypeFilteringKey: "alert_type",
            isLoading: false,
            error: null
        };
    }

    componentDidMount() {
        this.fetchAlertsFromBackend();
        this.props.changeRelatedNavbarButtons([
            <HelpButton key="help-button" tourContent={[]} />
        ]);
    }
    
    render() {
        return (
            <div className="content">
                <Row>
                    <Col xs="12" md="5" lg="4" xl="4">
                        <div>
                            <DateFilterManager
                                title="Filtro por Fechas:"
                                currentFromDate={this.state.fromDate}
                                currentToDate={this.state.toDate}
                                updateFromDate={this.updateFromDate}
                                updateToDate={this.updateToDate}
                                resetDates={this.resetDates}
                                activeQuantity={this.state.activeQuantity}
                                totalRegisters={this.state.filteredAlerts.length}
                            />
                        </div>
                        
                        <div className="mt-3">
                            <ChecksFilterManager
                                title="Tipo de Alerta:"
                                managerKey={"alertType"}
                                filteringOptions={this.getAlertTypes()}
                                originalData={this.state.originalAlerts}
                                filteringKey={this.state.alertTypeFilteringKey}
                                originalFilters={{}} // Empty filters means nothing selected
                                preventAutoSelect={true} // Add this to prevent auto-selection
                                filteredDataCallback={this.updateFilteredAlertsBasedOnType}
                                activeQuantity={this.state.activeQuantity}
                                totalRegisters={this.state.filteredAlerts.length}
                                itemRendering={(item) => {
                                    return (
                                        <span>
                                            {item.type_description}
                                        </span>
                                    );
                                }} />
                        </div>
                    </Col>

                    <Col xs="12" md="7" lg="8" xl="8">
                        <Card>
                            <CardBody>
                                <div className="card-without-scrollbar">
                                    <AlertsResumeTable
                                        activeQuantity={this.state.activeQuantity}
                                        alerts={this.state.filteredAlerts}
                                        isLoading={this.state.isLoading}
                                        error={this.state.error}
                                        updateActiveQuantity={this.updateActiveQuantity} />
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </div>
        );
    }

    // Methods
    fetchAlertsFromBackend = async () => {
        this.setState({ isLoading: true, error: null });
        window.document.body.style.cursor = "wait";
        
        try {
            // Call the alerts endpoint to get historical alerts using axios.get directly
            // This matches the working approach for resources endpoint
            const response = await axios.get(process.env.REACT_APP_ALERTS_API + '/changes');
            
            // Check if response data is wrapped in a "changes" parameter
            const changesData = response.data.changes || response.data;
            
            // Map the response data to the expected format for alerts
            const formattedAlerts = this.mapApiResponseToAlerts(changesData);
            
            // Sort by date descending (newest first)
            formattedAlerts.sort((a, b) => {
                const dateA = new Date(a.date);
                const dateB = new Date(b.date);
                return dateB - dateA;
            });
            
            this.setState({
                originalAlerts: formattedAlerts,
                filteredAlerts: [], // Initialize with empty array since no filters are selected
                isLoading: false
            }, () => {
                // After state is updated, apply date filtering only (no alert type filters yet)
                if (this.state.fromDate && this.state.toDate) {
                    const fromDate = new Date(this.state.fromDate);
                    const toDate = new Date(this.state.toDate);
                    // Set toDate to end of day to include the entire day
                    toDate.setHours(23, 59, 59, 999);
                    
                    const dateFilteredItems = this.state.originalAlerts.filter(alert => {
                        const alertDate = new Date(alert.date);
                        return alertDate >= fromDate && alertDate <= toDate;
                    });
                    
                    this.setState({
                        filteredAlerts: dateFilteredItems
                    });
                }
            });
        } catch (error) {
            console.error("Error fetching alerts:", error);
            this.setState({ 
                isLoading: false, 
                error: "Error al cargar las alertas. Por favor intente nuevamente."
            });
        } finally {
            window.document.body.style.cursor = "default";
        }
    }
    
    // Map API response to alerts format
    mapApiResponseToAlerts = (data) => {
        return data.map((item, index) => {
            // Handle both potential response formats
            // Format 1: {id, severity, type, timestamp, differences}
            // Format 2: {resource_id, type, timestamp, url}
            return {
                id: index + 1,
                // If severity is present use it, otherwise default to 'warning'
                status: item.severity === 'error' ? 'DANGER' : 'WARNING',
                // Use either source_id or resource_id
                source_id: item.id || item.resource_id,
                // Use source_id or resource_id as description
                description: item.id || item.resource_id || 'Unknown',
                date: item.timestamp,
                // Normalize the type to uppercase with underscores
                alert_type: item.type ? item.type.toUpperCase().replace(/\s/g, '_') : 'UNKNOWN',
                type_description: item.type || 'Unknown',
                // If differences isn't present but url is, show the url
                differences: item.differences || (item.url ? `URL: ${item.url}` : 'No additional details')
            };
        });
    }

    getAlertTypes = () => {
        // Get unique alert types from the originalAlerts
        const alertTypes = [...new Set(this.state.originalAlerts.map(alert => alert.alert_type))];
        
        // If no alerts yet, return default types
        if (alertTypes.length === 0) {
            return [
                { "alert_type": 'TERMS_UPDATE', "type_description": 'Actualización de TyC' }
            ];
        }
        
        // Map alert types to the expected format
        return alertTypes.map(type => {
            const alertWithType = this.state.originalAlerts.find(alert => alert.alert_type === type);
            return {
                "alert_type": type,
                "type_description": alertWithType ? alertWithType.type_description : type
            };
        });
    }

    updateActiveQuantity = (newQuantity) => {
        this.setState({ activeQuantity: newQuantity });
    }
    
    // Date filter methods
    updateFromDate = (newDate) => {
        this.setState({ fromDate: newDate }, () => {
            this.applyDateFilter();
        });
    }
    
    updateToDate = (newDate) => {
        this.setState({ toDate: newDate }, () => {
            this.applyDateFilter();
        });
    }
    
    resetDates = () => {
        const today = new Date();
        const oneMonthAgo = new Date();
        oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
        
        this.setState({
            fromDate: oneMonthAgo,
            toDate: today
        }, () => {
            this.applyDateFilter();
        });
    }
    
    applyDateFilter = () => {
        // Apply both date and type filters
        this.updateFilteredAlertsBasedOnType(this.state.alertTypeFilters);
    }

    updateFilteredAlertsBasedOnType = (newFilters) => {
        let filteredItems = [];
        
        // Store the new alert type filters
        this.setState({ alertTypeFilters: newFilters });
        
        // If no alert type filters are selected, return empty array
        if (Object.keys(newFilters).length === 0) {
            this.setState({ filteredAlerts: [] });
            return;
        }

        // 1. First filter by alert type
        filteredItems = this.state.originalAlerts.filter(alert => {
            return newFilters[alert.alert_type] === true;
        });
        
        // 2. Then filter by date range
        if (this.state.fromDate && this.state.toDate) {
            const fromDate = new Date(this.state.fromDate);
            const toDate = new Date(this.state.toDate);
            // Set toDate to end of day to include the entire day
            toDate.setHours(23, 59, 59, 999);
            
            filteredItems = filteredItems.filter(alert => {
                const alertDate = new Date(alert.date);
                return alertDate >= fromDate && alertDate <= toDate;
            });
        }
        
        // 3. Sort by date in descending order (newest first)
        filteredItems.sort((a, b) => {
            const dateA = new Date(a.date);
            const dateB = new Date(b.date);
            return dateB - dateA; // Descending order
        });
        
        this.setState({ filteredAlerts: filteredItems });
    }
}

const mapdispatchToProps = dispatch => {
    return {
        changeRelatedNavbarButtons: (navbarButtons) => dispatch({ type: "changeNavbarButtons", navbarButtons: navbarButtons }),
    };
};

const mapStateToProps = state => {
    return {
        activeUser: state.mainReducer.activeUser,
        coreBackendHost: state.mainReducer.coreBackendHost,
    };
};

export default connect(mapStateToProps, mapdispatchToProps)(withRouter(AlertsRegistry));